import {Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { InnFrontI18n } from 'inn-front-lib';
import { LanguageService } from '@core/services/language/language.service';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import { RouterOutlet, Router, NavigationEnd, RouterModule } from '@angular/router';
import { Title, DomSanitizer } from '@angular/platform-browser';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { filter, first, catchError, map } from 'rxjs/operators';
import { AuthService } from '@features/auth/auth.service';
import { GdpAccessService } from '@features/gdp/services/gdp-access.service';
import { PermissionService } from '@core/services/permission/permission.service';
import { PERMISSIONS } from '@core/services/permission/permission.model';
import { SuggestionFormDialogComponent } from '@features/gdp/components/suggestion-form-dialog/suggestion-form-dialog.component';
import { UsersService } from '@features/users/users.service';
import { UserAvatarComponent } from '@shared/components/user-avatar/user-avatar.component';
import { MatSidenav } from '@angular/material/sidenav';
import { LanguageSelectorComponent } from './shared/components/language-selector/language-selector.component';
import { DevToolsComponent } from './shared/components/dev-tools/dev-tools.component';
import { DevBottomBarComponent } from './shared/components/dev-tools/dev-bottom-bar/dev-bottom-bar.component';
import { TranslatePipe } from './shared/pipes/translate.pipe';
import { NotificationBellComponent } from '@core/notifications/notification-bell/notification-bell.component';
import { NotificationToastsComponent } from '@core/notifications/notification-toasts/notification-toasts.component';
import { TopLayerActionDirective } from './shared/directives/top-layer-action.directive';
import {HttpClient} from "@angular/common/http";
import { environment } from '../environments/environment';
import { of } from 'rxjs';

interface NavigationItem {
  title: string;
  route: string;
  icon: string;
  translationKey: string;
  children?: NavigationItem[];
  /** Si se define, el item solo se muestra cuando devuelve true (se evalúa como signal). */
  visible?: () => boolean;
  /** Acción rápida opcional mostrada a la derecha del item (no interfiere con la navegación). */
  action?: { icon: string; tooltipKey: string; run: () => void };
  /** Fuente del icono cuando el glifo no existe en Material Icons clásico (p.ej. 'material-symbols-outlined'). */
  fontSet?: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LanguageSelectorComponent,
    DevToolsComponent,
    DevBottomBarComponent,
    TranslatePipe,
    TopLayerActionDirective
    , UserAvatarComponent,
    NotificationBellComponent,
    NotificationToastsComponent, NgOptimizedImage],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  currentRoute = window.location.pathname;

  private readonly gdpAccess = inject(GdpAccessService);
  protected readonly usersService = inject(UsersService);
  private readonly permissions = inject(PermissionService);
  private readonly dialog = inject(MatDialog);

  navigationItems: NavigationItem[] = [
    { title: 'Dashboard', route: '/dashboard', icon: 'dashboard', translationKey: 'nav.dashboard' },
    { title: 'Directory', route: '/directory', icon: 'contacts', translationKey: 'nav.directory', visible: () => this.permissions.has(PERMISSIONS.ACCESS_DIRECTORY) },
    { title: 'Transport Quotes', route: '/transport-quotes', icon: 'request_quote', translationKey: 'nav.transport-quotes', visible: () => this.permissions.has(PERMISSIONS.ACCESS_QUOTES) },
    { title: 'Transport Orders', route: '/transport-orders', icon: 'local_shipping', translationKey: 'nav.transport-orders', visible: () => this.permissions.has(PERMISSIONS.ACCESS_ORDERS) },
    { title: 'Shipments', route: '/shipments', icon: 'inventory_2', translationKey: 'nav.shipments', visible: () => this.permissions.has(PERMISSIONS.ACCESS_SHIPMENT) },
    { title: 'Billing', route: '/billing', icon: 'receipt_long', translationKey: 'nav.billing', visible: () => this.permissions.has(PERMISSIONS.ACCESS_INVOICES) },
    { title: 'Income', route: '/income', icon: 'trending_up', translationKey: 'nav.income', visible: () => this.permissions.has(PERMISSIONS.ACCESS_INCOMES) },
    { title: 'Expenses', route: '/expenses', icon: 'trending_down', translationKey: 'nav.expenses', visible: () => this.permissions.has(PERMISSIONS.ACCESS_EXPENSES) },
    { title: 'Commissions', route: '/commissions', icon: 'percent', translationKey: 'nav.commissions', visible: () => this.permissions.has(PERMISSIONS.ACCESS_COMMISSIONS) },
    { title: 'DeCA', route: '/deca', icon: 'description', translationKey: 'nav.deca', visible: () => this.permissions.has(PERMISSIONS.ACCESS_DECA) },
    { title: 'Gestión', route: '/gdp', icon: 'strategy', fontSet: 'material-symbols-outlined', translationKey: 'nav.gdp', visible: () => this.permissions.has(PERMISSIONS.ACCESS_GDP) },
    {
      title: 'Sugerencias', route: '/suggestions', icon: 'lightbulb', translationKey: 'nav.suggestions',
      visible: () => this.permissions.has(PERMISSIONS.ACCESS_SUGGESTIONS),
      // Botón + para enviar una sugerencia sin salir de la página actual
      action: { icon: 'add', tooltipKey: 'nav.suggestions.quick-add', run: () => this.openSuggestionDialog() }
    },
    // Oculto en el sidebar (visible: false); solo aporta el título del toolbar en /notifications/*
    { title: 'Notificaciones', route: '/notifications', icon: 'notifications', translationKey: 'notifications.title', visible: () => false }
  ];

  visibleNavigationItems = computed(() =>
    this.navigationItems.filter(item => !item.visible || item.visible())
  );

  // Modo hamburguesa en móviles (Handset) o cuando la ventana del navegador
  // ocupa la mitad o menos del ancho de la pantalla física.
  isHandset$ = this.breakpointObserver.observe([
    Breakpoints.Handset,
    `(max-width: ${Math.floor(window.screen.width / 2)}px)`
  ]);
  /** Móvil o ventana estrecha: la barra lateral es un cajón superpuesto. */
  readonly handset = toSignal(this.isHandset$.pipe(map(state => state.matches)), {initialValue: false});
  /** Barra lateral contraída a iconos (escritorio), recordada en este navegador. */
  readonly rail = signal(localStorage.getItem('tms.sidebar.rail') === '1');
  /** Logo del sidebar: versión "cute" si la URL de entrada trae ?cute=1. */
  readonly sidebarLogo = new URLSearchParams(window.location.search).get('cute') === '1'
    ? 'app/shared/assets/cute_logo.png'
    : 'app/shared/assets/innporting-logo.svg';
  http = inject(HttpClient);

  private _deathStarOpen = localStorage.getItem('devTools.open') === 'true';
  get deathStarOpen(): boolean { return this._deathStarOpen; }
  set deathStarOpen(v: boolean) {
    this._deathStarOpen = v;
    localStorage.setItem('devTools.open', String(v));
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    public authService: AuthService,
    private titleService: Title,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {
    this.iconRegistry.addSvgIcon(
      'death-star',
      this.sanitizer.bypassSecurityTrustResourceUrl('app/shared/assets/custom-icons/death-star.svg')
    );

    // Los componentes de inn-front-lib siguen el idioma elegido en el TMS.
    const innI18n = inject(InnFrontI18n);
    const language = inject(LanguageService);
    effect(() => innI18n.setLocale(language.getCurrentLanguage()));

    // Carga /gdp/me al autenticarse (y limpia la caché al hacer logout)
    // para condicionar la visibilidad de la pestaña "Gestión".
    effect(() => {
      if (this.authService.isAuthenticatedSignal()) {
        if (!window.location.pathname.startsWith('/deca/edit/')) {
          this.permissions.load();
          this.gdpAccess.load();
          this.usersService.load();
        }
      } else {
        this.permissions.reset();
        this.gdpAccess.reset();
        this.usersService.reset();
      }
    });
  }

  ngOnInit() {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      this.titleService.setTitle('[LOCAL] TMS Dashboard');
    } else if (hostname.startsWith('dev.')) {
      this.titleService.setTitle('[DEV] TMS Dashboard');
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentRoute = event.urlAfterRedirects;
    });

    if (!window.location.pathname.startsWith('/deca/edit/')) this.loadGoogleMaps();
  }

  private loadGoogleMaps() {
    if (!(window as any).google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places,geometry`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  toggleRail(): void {
    const next = !this.rail();
    this.rail.set(next);
    try { localStorage.setItem('tms.sidebar.rail', next ? '1' : '0'); } catch { /* almacenamiento bloqueado */ }
  }

  isActive(item: NavigationItem): boolean {
    return this.currentRoute === item.route || this.currentRoute.startsWith(item.route + '/') || this.currentRoute.startsWith(item.route + '?');
  }

  /** Pantallas fuera del menú lateral: su título en la cabecera (antes caían en «Panel de control»). */
  private readonly extraTitles: { route: string; translationKey: string }[] = [
    { route: '/users', translationKey: 'nav.users' },
    { route: '/profile', translationKey: 'nav.profile' },
    { route: '/notifications', translationKey: 'notifications.title' }
  ];

  getPageTitle(): string {
    // Prefix-match (el más largo gana) para que las rutas hijas
    // (p.ej. /gdp/sprints/3/board) mantengan el título de su sección.
    const item = [...this.navigationItems, ...this.extraTitles]
      .filter(nav => this.currentRoute === nav.route || this.currentRoute.startsWith(nav.route + '/'))
      .sort((a, b) => b.route.length - a.route.length)[0];
    return item ? item.translationKey : 'nav.dashboard';
  }

  /** Abre el formulario de sugerencia en un diálogo, sin interrumpir el flujo de trabajo. */
  openSuggestionDialog(): void {
    // El "+" del sidebar es pulsable con modales abiertos: no abrir duplicados
    if (this.dialog.openDialogs.some(ref => ref.componentInstance instanceof SuggestionFormDialogComponent)) {
      return;
    }
    this.dialog.open(SuggestionFormDialogComponent, {
      width: '720px',
      maxWidth: '94vw',
      autoFocus: false
    });
  }

  closeSidenavOnHandset(drawer: MatSidenav) {
    this.isHandset$.pipe(first()).subscribe(result => {
      if (result.matches) {
        drawer.toggle();
      }
    });
  }

  logout() {
    this.authService.logout().subscribe();
    this.router.navigate(['/login']);
  }

  /** Bloquear la pantalla (como inn-dash): se vuelve con la contraseña al mismo sitio. */
  lock(): void {
    const me = this.usersService.me();
    const mail = me?.mail || this.authService.getCurrentUser()?.email;
    if (!mail) {
      // Sin saber de quién es la sesión no se podría desbloquear: se cierra sin más.
      this.logout();
      return;
    }
    // Nada de la sesión debe quedar a la vista detrás del bloqueo.
    this.dialog.closeAll();
    this.authService.lock({
      mail,
      name: me ? `${me.name || ''} ${me.surname || ''}`.trim() : '',
      accountId: me?.id ?? null,
      returnUrl: this.router.url
    });
  }

  protected readonly environment = environment;
  protected readonly window = window;
}
